import { useMemo } from 'react';
import { EEthereumNetworkId } from '@ankr.com/provider';
import { CircularProgress, FormControlLabel, Switch } from '@mui/material';
import Typography from '@mui/material/Typography';
import BigNumber from 'bignumber.js';
import cronstrue from 'cronstrue';
import { makeStyles } from 'tss-react/mui';

import { useChainData } from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import {
  useGetAccountAddressQuery,
  useSwitchNetworkMutation,
} from 'modules/store';

import { useToggleTaskMutation } from '../../actions/toggleTask';
import { Signature } from '../Signature';
import translation from './taskView.translation';

interface Props {
  taskId: string;
  funcName?: string;
  params?: string[];
  cronTarget?: string;
  cronDate?: string;
  amountSpent: BigNumber;
  chainId: EEthereumNetworkId;
  isPaused: boolean;
}

const mergedTranslation = mergeTranslations(globalTranslation, translation);

const EMPTY_PARAMS: string[] = [];
export function TaskView({
  taskId,
  chainId,
  isPaused,
  cronTarget,
  cronDate,
  amountSpent,
  funcName,
  params = EMPTY_PARAMS,
}: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);
  const currentChainData = useChainData();
  const chainData = useChainData(chainId);

  const isSwitchNetwork =
    currentChainData?.chainId &&
    chainData?.chainId &&
    currentChainData.chainId !== chainData.chainId;

  const [toggleTask, { isLoading: isToggleLoading }] = useToggleTaskMutation();
  const [switchNetwork, { isLoading: isSwitchNetworkLoading }] =
    useSwitchNetworkMutation();
  const isLoading = isToggleLoading || isSwitchNetworkLoading;

  const { data: accountAddress = '' } = useGetAccountAddressQuery();

  const date = useMemo(() => {
    if (!cronDate) {
      return null;
    }

    return cronstrue.toString(cronDate);
  }, [cronDate]);

  return (
    <div data-tid="view-task" className={classes.container}>
      <div className={classes.details}>
        <Typography variant="h6">{t(keys.details)}</Typography>

        <FormControlLabel
          label={
            <Typography
              variant="button2"
              color={theme => theme.palette.grey[600]}
              ml={2}
            >
              {isLoading
                ? t(keys.loading)
                : t(isPaused ? keys.paused : keys.active)}
            </Typography>
          }
          control={
            isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Switch
                data-tid="view-task-paused-switch"
                checked={!isPaused}
                onClick={async () => {
                  if (isSwitchNetwork) {
                    await switchNetwork({ chainId: chainData.chainId });
                  }
                  await toggleTask({
                    taskId,
                    accountAddress,
                    chainId: chainData?.chainId ?? null,
                  });
                }}
              />
            )
          }
        />
      </div>

      <div className={classes.list}>
        {cronTarget && (
          <div className={classes.listItem}>
            <Typography variant="subtitle3" mr={2}>
              {t(keys.execute)}
            </Typography>

            <div className={classes.values}>
              <Typography
                data-tid="view-task-cron-target"
                variant="body2"
                className={classes.text}
              >
                {cronTarget}
              </Typography>

              {funcName && (
                <Signature
                  data-tid="view-task-function-name"
                  variant="body2"
                  value={funcName}
                  className={classes.text}
                >
                  {funcName}
                </Signature>
              )}

              {params.map((parameter, i) => (
                <Typography
                  key={
                    // eslint-disable-next-line react/no-array-index-key
                    i
                  }
                  data-tid={`view-task-params-item-${i}`}
                  variant="body2"
                  className={classes.text}
                >
                  {parameter}
                </Typography>
              ))}
            </div>
          </div>
        )}

        {date && (
          <div className={classes.listItem}>
            <Typography variant="subtitle3" mr={2}>
              {t(keys.when)}
            </Typography>

            <Typography
              data-tid="view-task-date"
              variant="body2"
              component="div"
              className={classes.text}
            >
              {date}
            </Typography>
          </div>
        )}

        {amountSpent && (
          <div className={classes.listItem}>
            <Typography variant="subtitle3" mr={2}>
              {t(keys.totalSpent)}
            </Typography>

            <div className={cx(classes.values, classes.text)}>
              <Typography
                data-tid="view-task-total-spent"
                variant="subtitle2"
                component="div"
              >
                {t(keys.units.tokenAmount, {
                  amount: amountSpent.toString(),
                  token: chainData?.tokenName,
                })}
              </Typography>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    marginTop: theme.spacing(12),
    display: 'flex',
    flexDirection: 'column',
  },

  details: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  text: {
    '&&': {
      wordBreak: 'break-all',
      textAlign: 'right',
    },
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(8),
  },

  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(5, 0),

    '&:not(:first-child)': {
      borderTop: `1px solid ${theme.palette.grey[100]}`,
    },
  },
  values: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
}));
